import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Search, Loader2, X, ChevronRight,
  ClipboardCheck, Save, CheckCircle2, AlertCircle,
  FileText, Calendar, Plus, Clock, Shield, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import ChecklistEngine from "../components/ChecklistEngine";

import { LOGO_URL } from "../config/constants";

const STATUS_BADGES = {
  draft: { label: "Draft", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  pending_review: { label: "Pending Review", cls: "bg-status-amber/15 text-status-amber border-amber-500/30" },
  approved: { label: "Approved", cls: "bg-teal/15 text-teal border-teal/30" },
  completed: { label: "Completed", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
};

const TX_OPTIONS = [
  { value: "purchase", label: "Purchase" },
  { value: "sale", label: "Sale" },
  { value: "linked", label: "Linked (Sale & Purchase)" },
];

// BM-H4191: FORF matter type -> refinance checklist transaction type.
const REFINANCE_TX_FROM_Q9 = { remortgage: "remortgage", remortgage_toe: "remortgage_toe", toe_only: "toe" };
const COMPANY_CLIENT_TYPES = ["company", "llp"];

export default function ChecklistPage({ config, checklistType }) {
  const navigate = useNavigate();
  // BM-H4191: the remortgage / transfer of equity checklist brings its own
  // transaction types and a company switch in place of "Mortgaged".
  const isRefinance = checklistType === "refinance_completion";
  const txOptions = config.txOptions || TX_OPTIONS;
  const defaultTx = config.defaultTx || "purchase";
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("id");

  // State
  const [step, setStep] = useState(resumeId ? "loading" : "select");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [existingChecklists, setExistingChecklists] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Checklist state
  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState({});
  const [transactionType, setTransactionType] = useState(defaultTx);
  const [isCompany, setIsCompany] = useState(false);
  const [isLeasehold, setIsLeasehold] = useState(false);
  const [isMortgaged, setIsMortgaged] = useState(false);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [linkedCB, setLinkedCB] = useState(null);

  // Setup form state
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [setupTx, setSetupTx] = useState(defaultTx);
  const [setupCompany, setSetupCompany] = useState(false);
  const [setupMatterType, setSetupMatterType] = useState("");
  const [setupClientType, setSetupClientType] = useState("");
  const [setupLeasehold, setSetupLeasehold] = useState(false);
  const [setupMortgaged, setSetupMortgaged] = useState(false);

  // Pre-exchange date comparison (pre-completion only)
  const [preExchangeDate, setPreExchangeDate] = useState(null);
  const [dateConflictReason, setDateConflictReason] = useState("");

  const saveTimerRef = useRef(null);

  // Load existing checklists on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/checklists/list`, { params: { checklist_type: checklistType } });
        setExistingChecklists((res.data || []).filter(c => !c.deleted));
      } catch { /* ignore */ }
      setLoadingExisting(false);
    })();
  }, [checklistType]);

  // Resume a checklist by ID
  useEffect(() => {
    if (!resumeId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/checklists/${resumeId}`);
        loadChecklist(res.data);
      } catch {
        toast.error("Could not load checklist");
        setStep("select");
      }
    })();
  }, [resumeId]);

  const loadChecklist = (doc) => {
    setChecklist(doc);
    setItems(doc.items || {});
    setTransactionType(doc.transaction_type || defaultTx);
    setIsCompany(!!doc.is_company);
    setIsLeasehold(doc.is_leasehold || false);
    setIsMortgaged(doc.is_mortgaged || false);
    setSignature(doc.fee_earner_signature || null);
    setStep("checklist");
    // For pre-completion, look up linked completions book entry + pre-exchange date
    if ((checklistType === "pre_completion" || isRefinance) && doc.matter_reference) {
      axios.get(`${API}/completionsbook/by-matter/${encodeURIComponent(doc.matter_reference)}`)
        .then(res => setLinkedCB(res.data))
        .catch(() => {});
    }
    if (checklistType === "pre_completion" && doc.matter_reference) {
      // Fetch the pre-exchange source date for comparison
      axios.get(`${API}/completionsbook/source-date/${encodeURIComponent(doc.matter_reference)}`)
        .then(res => {
          if (res.data.pre_exchange_date) {
            setPreExchangeDate(res.data.pre_exchange_date);
          }
        })
        .catch(() => {});
    }
  };

  // Matter search
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await axios.post(`${API}/hub/ai-search`, { query: query.trim(), limit: 10 });
      setSearchResults(res.data.results || []);
      if (!res.data.results?.length) toast.info("No matters found.");
    } catch {
      toast.error("Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectMatter = (matter) => {
    setSelectedMatter(matter);
    // Pre-fill transaction type from matter data if available
    const fd = matter.data?.formdata || matter.data?.header || {};
    if (isRefinance) {
      const q9 = [matter.data?.specific_matter_type, matter.specific_matter_type, matter.data?.Q9, fd.Q9]
        .find(v => REFINANCE_TX_FROM_Q9[v]) || "";
      const clientType = (matter.data?.client_type || fd.client_type || matter.data?.entity_type || "").toLowerCase();
      if (REFINANCE_TX_FROM_Q9[q9]) setSetupTx(REFINANCE_TX_FROM_Q9[q9]);
      setSetupMatterType(REFINANCE_TX_FROM_Q9[q9] ? q9 : "");
      setSetupClientType(clientType);
      setSetupCompany(COMPANY_CLIENT_TYPES.includes(clientType));
    } else if (fd.transaction_type?.includes("sale")) setSetupTx("sale");
    else if (fd.transaction_type?.includes("purchase")) setSetupTx("purchase");
    setStep("setup");
  };

  // Create new checklist
  const handleCreate = async () => {
    setSaving(true);
    try {
      const matterRef = selectedMatter?.secondary_label || selectedMatter?.matter_reference || selectedMatter?.data?.header?.matter_reference || "";
      const clientName = selectedMatter?.primary_label || selectedMatter?.data?.header?.client_name || "";
      const feeEarner = selectedMatter?.data?.header?.fee_earner || "";

      const res = await axios.post(`${API}/checklists/create`, {
        checklist_type: checklistType,
        matter_id: selectedMatter?.id || null,
        matter_reference: matterRef,
        client_name: clientName,
        fee_earner: feeEarner,
        transaction_type: setupTx,
        is_leasehold: setupLeasehold,
        is_mortgaged: setupMortgaged,
        ...(isRefinance ? {
          is_company: setupCompany,
          client_type: setupClientType || (setupCompany ? "company" : "individual"),
          forf_matter_type: setupMatterType || undefined,
          forf_submission_id: selectedMatter?.id || undefined,
        } : {}),
      });
      loadChecklist(res.data);
      toast.success("Checklist created");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to create checklist"));
    } finally {
      setSaving(false);
    }
  };

  // Auto-save with debounce
  const saveToBackend = useCallback(async (updatedItems) => {
    if (!checklist?.id) return;
    setSaving(true);
    try {
      const payload = { items: updatedItems };
      // If pre-completion and item 8.1 date conflicts with pre-exchange date, include audit data
      if (checklistType === "pre_completion" && preExchangeDate && updatedItems["8.1"]?.date && updatedItems["8.1"].date !== preExchangeDate) {
        payload.completion_date_overridden = true;
        payload.completion_date_change_reason = dateConflictReason;
        const existingHistory = checklist.completion_date_history || [];
        const alreadyLogged = existingHistory.some(h => h.new_date === updatedItems["8.1"].date);
        if (!alreadyLogged) {
          payload.completion_date_history = [...existingHistory, {
            old_date: preExchangeDate,
            new_date: updatedItems["8.1"].date,
            changed_by: checklist.fee_earner || "",
            changed_at: new Date().toISOString(),
            reason: dateConflictReason,
          }];
        }
      }
      const res = await axios.put(`${API}/checklists/${checklist.id}`, payload);
      setChecklist(res.data);
      setLastSaved(new Date());
    } catch {
      toast.error("Auto-save failed");
    } finally {
      setSaving(false);
    }
  }, [checklist?.id, checklistType, preExchangeDate, dateConflictReason, checklist]);

  const handleItemChange = useCallback((fieldId, fieldData) => {
    setItems(prev => {
      const next = { ...prev, [fieldId]: fieldData };
      // Debounced save
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveToBackend(next), 1200);
      return next;
    });
  }, [saveToBackend]);

  // Sign-off
  const handleSign = async (name) => {
    if (!checklist?.id || !name.trim()) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/checklists/${checklist.id}`, {
        fee_earner_signature: name.trim(),
        status: "pending_review",
      });
      setChecklist(res.data);
      setSignature(name.trim());
      toast.success("Signed and submitted for review");
    } catch {
      toast.error("Failed to sign");
    } finally {
      setSaving(false);
    }
  };

  // Update config toggles
  const handleConfigChange = async (field, value) => {
    if (!checklist?.id) return;
    const updates = {};
    if (field === "transaction_type") { setTransactionType(value); updates.transaction_type = value; }
    if (field === "is_leasehold") { setIsLeasehold(value); updates.is_leasehold = value; }
    if (field === "is_mortgaged") { setIsMortgaged(value); updates.is_mortgaged = value; }
    if (field === "is_company") { setIsCompany(value); updates.is_company = value; }
    try {
      await axios.put(`${API}/checklists/${checklist.id}`, updates);
    } catch { /* silent */ }
  };

  // Submit for supervisor review
  const handleSubmitForReview = async () => {
    if (!checklist?.id) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/checklists/${checklist.id}`, { status: "pending_review" });
      setChecklist(res.data);
      toast.success("Submitted for supervisor review");
    } catch {
      toast.error("Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  // Supervisor approve
  const handleApprove = async () => {
    if (!checklist?.id) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/checklists/${checklist.id}`, {
        status: "approved",
        supervisor_review: { status: "approved", reviewed_at: new Date().toISOString() },
      });
      setChecklist(res.data);
      toast.success("Checklist approved");
    } catch (err) {
      // BM-H613: an open deferred conflict check blocks exchange; the server
      // says so (409 deferred_conflict_outstanding). Show its reason, not a
      // generic failure.
      toast.error(errorMessage(err, "Failed to approve"));
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = config.title;
  const isPreExchange = checklistType === "pre_exchange";

  // ── STEP: Loading ──
  if (step === "loading") {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal" />
      </div>
    );
  }

  // ── STEP: Select Matter ──
  if (step === "select") {
    return (
      <div className="min-h-screen bg-navy">
        <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
          <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai" className="h-7" />
              <span className="text-white font-bold text-sm">{pageTitle}</span>
            </div>
            <Button variant="ghost" onClick={() => navigate("/")} className="text-txt-muted hover:text-teal" data-testid="checklist-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-in-up">
          {/* Existing Checklists */}
          {loadingExisting ? (
            <div className="glass-card p-6 mb-8 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-teal mx-auto" />
            </div>
          ) : existingChecklists.length > 0 && (
            <div className="glass-card p-5 mb-8" data-testid="existing-checklists">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal" />
                Resume Existing Checklist
              </h2>
              <div className="space-y-2">
                {existingChecklists.map(cl => {
                  const badge = STATUS_BADGES[cl.status] || STATUS_BADGES.draft;
                  return (
                    <button
                      key={cl.id}
                      onClick={() => { navigate(`?id=${cl.id}`); loadChecklist(cl); }}
                      className="w-full glass-card p-3 rounded-md text-left flex items-center justify-between hover:border-teal/40 transition-colors group"
                      data-testid={`resume-checklist-${cl.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ClipboardCheck className="w-4 h-4 text-txt-muted flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-white block truncate">
                            {cl.matter_reference || "No matter ref"} — {cl.client_name || "Unknown client"}
                          </span>
                          <span className="text-xs text-txt-muted block mt-0.5">
                            {cl.transaction_type} · Created {new Date(cl.created_at).toLocaleDateString("en-GB")}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${badge.cls}`}>{badge.label}</span>
                        <ChevronRight className="w-4 h-4 text-txt-muted group-hover:text-teal transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* New Checklist — Matter Search */}
          <div className="glass-card p-5" data-testid="matter-search-section">
            <h2 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
              <Plus className="w-4 h-4 text-teal" />
              New {config.newLabel || (isPreExchange ? "Pre-Exchange" : "Pre-Completion")} Checklist
            </h2>
            <p className="text-xs text-txt-muted mb-4">Search for the matter to begin.</p>
            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. 'VEC0089', 'Smith purchase'"
                  className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted pr-8"
                  data-testid="checklist-matter-search-input"
                />
                {query && (
                  <button type="button" onClick={() => { setQuery(""); setSearchResults(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-txt-muted hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button type="submit" disabled={searching || !query.trim()} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="checklist-matter-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </Button>
            </form>

            {searchResults && searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map((m, i) => (
                  <button
                    key={m.id || i}
                    onClick={() => handleSelectMatter(m)}
                    className="w-full glass-card p-3 rounded-md text-left flex items-center justify-between hover:border-teal/40 transition-colors group"
                    data-testid={`checklist-matter-result-${i}`}
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-white block truncate">{m.primary_label || "Unknown"}</span>
                      <span className="text-xs text-txt-muted block mt-0.5 truncate">{m.secondary_label || ""} · {m.form_type || ""}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-txt-muted group-hover:text-teal flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            {searchResults && searchResults.length === 0 && (
              <p className="text-sm text-txt-muted text-center py-4">No matters found. Try a different search term.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── STEP: Setup (configure transaction type etc) ──
  if (step === "setup") {
    return (
      <div className="min-h-screen bg-navy">
        <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
          <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai" className="h-7" />
              <span className="text-white font-bold text-sm">{pageTitle}</span>
            </div>
            <Button variant="ghost" onClick={() => setStep("select")} className="text-txt-muted hover:text-teal">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-8 animate-fade-in-up">
          <div className="glass-card p-6" data-testid="checklist-setup-form">
            <h2 className="text-lg font-bold text-white mb-1">Configure Checklist</h2>
            <p className="text-xs text-txt-muted mb-6">
              Matter: <span className="text-white font-medium">{selectedMatter?.primary_label}</span>
              {selectedMatter?.secondary_label && <span className="text-txt-muted"> — {selectedMatter.secondary_label}</span>}
            </p>

            {/* Transaction Type */}
            <div className="mb-5">
              <label className="text-sm font-medium text-txt block mb-2">Transaction Type</label>
              <div className="flex flex-wrap gap-2">
                {txOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSetupTx(opt.value)}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      setupTx === opt.value
                        ? "bg-teal border-teal/50 text-white"
                        : "border-navy-border text-txt-muted hover:border-teal/30 hover:text-white"
                    }`}
                    data-testid={`setup-tx-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => setSetupLeasehold(!setupLeasehold)}
                className={`p-3 rounded-md border text-left transition-colors ${
                  setupLeasehold
                    ? "bg-teal/20 border-teal/40 text-white"
                    : "border-navy-border text-txt-muted hover:border-teal/20"
                }`}
                data-testid="setup-leasehold-toggle"
              >
                <span className="text-sm font-medium block">Leasehold</span>
                <span className="text-xs text-txt-muted">{setupLeasehold ? "Yes — extra sections shown" : "No"}</span>
              </button>
              {isRefinance ? (
              <button
                onClick={() => setSetupCompany(!setupCompany)}
                className={`p-3 rounded-md border text-left transition-colors ${
                  setupCompany
                    ? "bg-teal/20 border-teal/40 text-white"
                    : "border-navy-border text-txt-muted hover:border-teal/20"
                }`}
                data-testid="setup-company-toggle"
              >
                <span className="text-sm font-medium block">Company client</span>
                <span className="text-xs text-txt-muted">{setupCompany ? "Yes: board approval, execution and Companies House steps shown" : "No: individual client"}</span>
              </button>
              ) : (
              <button
                onClick={() => setSetupMortgaged(!setupMortgaged)}
                className={`p-3 rounded-md border text-left transition-colors ${
                  setupMortgaged
                    ? "bg-teal/20 border-teal/40 text-white"
                    : "border-navy-border text-txt-muted hover:border-teal/20"
                }`}
                data-testid="setup-mortgaged-toggle"
              >
                <span className="text-sm font-medium block">Mortgaged</span>
                <span className="text-xs text-txt-muted">{setupMortgaged ? "Yes — mortgage items shown" : "No"}</span>
              </button>
              )}
            </div>

            <Button
              onClick={handleCreate}
              disabled={saving}
              className="w-full bg-teal hover:bg-teal-hover text-white gap-2"
              data-testid="create-checklist-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
              Create Checklist
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP: Checklist (main editor) ──
  const statusBadge = STATUS_BADGES[checklist?.status] || STATUS_BADGES.draft;

  return (
    <div className="min-h-screen bg-navy">
      {/* Nav */}
      <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai" className="h-7" />
            <span className="text-white font-bold text-sm truncate">{pageTitle}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${statusBadge.cls}`}>{statusBadge.label}</span>
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-xs text-txt-muted flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span>}
            {!saving && lastSaved && <span className="text-xs text-txt-muted flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-teal" /> Saved</span>}
            <Button variant="ghost" onClick={() => navigate("/")} className="text-txt-muted hover:text-teal" data-testid="checklist-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-6 animate-fade-in-up">
        {/* Matter Info Header */}
        <div className="glass-card p-4 mb-4 flex items-center justify-between" data-testid="checklist-matter-header">
          <div>
            <p className="text-sm font-bold text-white">{checklist?.matter_reference || "No matter reference"}</p>
            <p className="text-xs text-txt-muted">{checklist?.client_name} · {checklist?.fee_earner}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Transaction type pills */}
            <div className="flex gap-1">
              {txOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleConfigChange("transaction_type", opt.value)}
                  className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-colors ${
                    transactionType === opt.value
                      ? "bg-teal border-teal/50 text-white"
                      : "border-navy-border text-txt-muted hover:text-white"
                  }`}
                  data-testid={`tx-pill-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Toggle pills */}
            <button
              onClick={() => handleConfigChange("is_leasehold", !isLeasehold)}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-colors ${
                isLeasehold ? "bg-blue-500/20 border-blue-500/40 text-blue-300" : "border-navy-border text-txt-muted"
              }`}
              data-testid="toggle-leasehold"
            >
              Leasehold
            </button>
            {isRefinance ? (
            <button
              onClick={() => handleConfigChange("is_company", !isCompany)}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-colors ${
                isCompany ? "bg-purple-500/20 border-purple-500/40 text-purple-300" : "border-navy-border text-txt-muted"
              }`}
              data-testid="toggle-company"
            >
              Company
            </button>
            ) : (
            <button
              onClick={() => handleConfigChange("is_mortgaged", !isMortgaged)}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-colors ${
                isMortgaged ? "bg-purple-500/20 border-purple-500/40 text-purple-300" : "border-navy-border text-txt-muted"
              }`}
              data-testid="toggle-mortgaged"
            >
              Mortgaged
            </button>
            )}
          </div>
        </div>

        {/* CQS Compliance Footer */}
        {config.pdfFooter && (
          <div className="glass-card p-3 mb-4 flex items-start gap-2" data-testid="cqs-compliance-banner">
            <Shield className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-txt-muted leading-relaxed">{config.pdfFooter}</p>
          </div>
        )}

        {/* Date Conflict Warning (pre-completion only) */}
        {checklistType === "pre_completion" && preExchangeDate && items["8.1"]?.date && items["8.1"].date !== preExchangeDate && (
          <div className="glass-card p-4 mb-4 border-amber-500/30 bg-status-amber/5" data-testid="date-conflict-warning">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-status-amber flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-status-amber">Completion date differs from Pre-Exchange</p>
                <p className="text-xs text-txt-muted mt-0.5">
                  Pre-Exchange date: <span className="text-white font-medium">{preExchangeDate}</span> &rarr;
                  This checklist: <span className="text-status-amber font-medium">{items["8.1"].date}</span>
                </p>
              </div>
            </div>
            <div className="ml-6">
              <p className="text-[11px] text-txt-muted mb-1.5">Please record the reason for this date change:</p>
              <textarea
                value={dateConflictReason}
                onChange={e => setDateConflictReason(e.target.value)}
                placeholder="e.g. Completion date moved by mutual agreement..."
                rows={2}
                className="w-full px-3 py-2 text-xs rounded-md bg-[rgba(26,37,71,0.85)] border border-amber-500/30 text-white placeholder:text-txt-muted focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="date-conflict-reason"
              />
            </div>
          </div>
        )}

        {/* The Checklist Engine */}
        <ChecklistEngine
          config={config}
          items={items}
          onItemChange={handleItemChange}
          transactionType={transactionType}
          isLeasehold={isLeasehold}
          isMortgaged={isMortgaged}
          isCompany={isRefinance && isCompany}
          readOnly={checklist?.status === "approved" || checklist?.status === "completed"}
          onSign={handleSign}
          signature={signature}
          matterReference={checklist?.matter_reference || ""}
          clientName={checklist?.client_name || ""}
        />

        {/* Action Bar */}
        {checklist?.status === "draft" && signature && (
          <div className="glass-card p-4 mt-4 flex items-center justify-between" data-testid="checklist-action-bar">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-teal" />
              <span className="text-sm text-white">Signed by {signature}</span>
            </div>
            <Button onClick={handleSubmitForReview} disabled={saving} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="submit-for-review-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Submit for Review
            </Button>
          </div>
        )}

        {checklist?.status === "pending_review" && (
          <div className="glass-card p-4 mt-4 flex items-center justify-between" data-testid="checklist-review-bar">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-status-amber" />
              <span className="text-sm text-status-amber">Awaiting supervisor review</span>
            </div>
            <Button onClick={handleApprove} disabled={saving} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="approve-checklist-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve
            </Button>
          </div>
        )}

        {checklist?.status === "approved" && (
          <div className="glass-card p-4 mt-4 flex items-center gap-2" data-testid="checklist-approved-bar">
            <CheckCircle2 className="w-5 h-5 text-teal" />
            <span className="text-sm text-teal font-medium">
              Approved — {isPreExchange ? "ready to exchange" : "ready to complete"}
            </span>
          </div>
        )}

        {/* Completions Book link (pre-completion only) */}
        {!isPreExchange && checklist && (
          <div className="glass-card p-4 mt-4 flex items-center justify-between" data-testid="checklist-cb-link">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-300" />
              <span className="text-sm text-txt-muted">Completions Book</span>
            </div>
            {linkedCB?.found ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/completions-book/${linkedCB.id}`)}
                className="border-navy-border text-purple-300 hover:bg-navy-hover"
                data-testid="checklist-view-cb-btn"
              >
                View / Update Completions Book Entry
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/completions-book/new?matter=${encodeURIComponent(checklist.matter_reference || "")}`)}
                className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white"
                data-testid="checklist-create-cb-btn"
              >
                Create Completions Book Entry
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
